import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";

// Step icons are recolored like the mobile app: blue for completed and current steps, gray for
// upcoming ones. The CMS icons are on another origin (no CORS), so they can't be fetched and edited
// like on mobile. Instead an SVG filter paints the icon's dark pixels in the step color and drops
// white ones, so a CMS icon with a white background shape doesn't turn into a solid disc.
export const STEP_ICON_ACTIVE_COLOR = "#026CE3";
const STEP_ICON_INACTIVE_COLOR = "#898A8D";
const STEP_ICON_ACTIVE_FILTER_ID = "installation-step-icon-active";
const STEP_ICON_INACTIVE_FILTER_ID = "installation-step-icon-inactive";

export type TrackerStep = {
  label: string;
  icon: string;
  // Bundled icon used when `icon` (usually a CMS URL) fails to load.
  fallbackIcon?: string;
};

const StepIconColorFilters = () => (
  <Box
    component="svg"
    aria-hidden="true"
    focusable="false"
    sx={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
    {[
      { id: STEP_ICON_ACTIVE_FILTER_ID, color: STEP_ICON_ACTIVE_COLOR },
      { id: STEP_ICON_INACTIVE_FILTER_ID, color: STEP_ICON_INACTIVE_COLOR },
    ].map(({ id, color }) => (
      <filter key={id} id={id} colorInterpolationFilters="sRGB">
        {/* Alpha = 2.5 x (alpha - luminance): opaque on dark lines, clear on white. */}
        <feColorMatrix
          in="SourceGraphic"
          type="matrix"
          values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  -0.53 -1.79 -0.18 2.5 0"
          result="matte"
        />
        <feFlood floodColor={color} />
        <feComposite in2="matte" operator="in" />
      </filter>
    ))}
  </Box>
);

const StepIcon = ({
  icon,
  fallbackIcon,
  isActiveOrCompleted,
  size,
}: {
  icon: string;
  fallbackIcon?: string;
  isActiveOrCompleted: boolean;
  size: number;
}) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [icon]);

  return (
    <Box
      component="img"
      src={failed && fallbackIcon ? fallbackIcon : icon}
      alt=""
      onError={() => setFailed(true)}
      sx={{
        width: size,
        height: size,
        filter: `url(#${
          isActiveOrCompleted ? STEP_ICON_ACTIVE_FILTER_ID : STEP_ICON_INACTIVE_FILTER_ID
        })`,
      }}
    />
  );
};

// Blue step bar shared by the dispatch and self-install trackers (like the mobile TrackerSteps).
export const TrackerSteps = ({
  steps,
  activeStep,
  ariaLabel,
  iconSize = 26,
}: {
  steps: TrackerStep[];
  activeStep: number;
  ariaLabel: string;
  // Icon size in px inside the 50px step circle. Trackers with few steps have room for bigger
  // icons (the self-install tracker passes a larger value, like the mobile app).
  iconSize?: number;
}) => (
  <>
    <StepIconColorFilters />
    <Box
      component="ol"
      aria-label={ariaLabel}
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`,
        m: 0,
        px: { xs: 1.25, sm: 2 },
        pt: 3,
        pb: 2,
        listStyle: "none",
        backgroundColor: "secondary.main",
      }}>
      {steps.map((step, index) => (
        <Box
          component="li"
          // CMS labels can repeat or be blank, so the index keeps keys unique.
          key={`${index}-${step.label}`}
          sx={{
            position: "relative",
            zIndex: 0,
            display: "flex",
            minWidth: 0,
            flexDirection: "column",
            alignItems: "center",
            color: "#fff",
            textAlign: "center",
            "&:not(:last-of-type)::after": {
              position: "absolute",
              zIndex: 0,
              top: 25,
              left: "calc(50% + 25px)",
              width: "calc(100% - 50px)",
              borderTop: "2px dashed rgba(255, 255, 255, 0.75)",
              content: '""',
            },
          }}>
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              width: 50,
              height: 50,
              flex: "0 0 50px",
              alignItems: "center",
              justifyContent: "center",
              border: index === activeStep ? "4px solid #ffc600" : "2px solid #fff",
              borderRadius: "50%",
              backgroundColor: "#fff",
            }}>
            <StepIcon
              icon={step.icon}
              fallbackIcon={step.fallbackIcon}
              isActiveOrCompleted={index <= activeStep}
              size={iconSize}
            />
          </Box>
          <Typography
            component="span"
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: { xs: 62, sm: 86 },
              mt: 1,
              fontSize: { xs: 11, sm: 14 },
              fontWeight: 600,
              lineHeight: 1.1,
              whiteSpace: "pre-line",
            }}>
            {step.label}
          </Typography>
        </Box>
      ))}
    </Box>
  </>
);
